/**
 * Cleanup Tools - 데이터 정리 도구
 */

import { Trans, t } from '@lingui/macro';
import {
    Trash2, Bell, Clock, Users, FileText, AlertTriangle,
    Loader2, Play, CheckCircle, XCircle
} from 'lucide-react';
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Fire, getFunc } from '../../services/FireClient';
import { collection, getDocs, query, where, deleteDoc, doc, Timestamp } from 'firebase/firestore';

interface CleanupTask {
    id: string;
    name: string;
    description: string;
    icon: React.ElementType;
    color: string;
    action: () => Promise<CleanupResult>;
}

interface CleanupResult {
    found: number;
    deleted: number;
    errors: string[];
}

const DevAdminCleanup: React.FC = () => {
    const [running, setRunning] = useState<string | null>(null);
    const [results, setResults] = useState<Record<string, CleanupResult>>({});
    const [dryRun, setDryRun] = useState(true);

    // 180일 이전 Notifications 정리
    const cleanupOldNotifications = async (): Promise<CleanupResult> => {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - 180);
        const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

        const snap = await getDocs(
            query(
                collection(Fire.store, 'notifications'),
                where('createdAt', '<', cutoffTimestamp)
            )
        );

        const found = snap.size;
        let deleted = 0;
        const errors: string[] = [];

        if (!dryRun) {
            for (const docSnap of snap.docs) {
                try {
                    await deleteDoc(docSnap.ref);
                    deleted++;
                } catch (error: any) {
                    errors.push(`${docSnap.id}: ${error.message}`);
                }
            }
        }

        return { found, deleted: dryRun ? 0 : deleted, errors };
    };

    // 만료된 Room Invites 정리
    const cleanupExpiredInvites = async (): Promise<CleanupResult> => {
        const now = Timestamp.now();

        const snap = await getDocs(
            query(
                collection(Fire.store, 'roominvites'),
                where('expiresAt', '<', now)
            )
        );

        const found = snap.size;
        let deleted = 0;
        const errors: string[] = [];

        if (!dryRun) {
            for (const docSnap of snap.docs) {
                try {
                    await deleteDoc(docSnap.ref);
                    deleted++;
                } catch (error: any) {
                    errors.push(`${docSnap.id}: ${error.message}`);
                }
            }
        }

        return { found, deleted: dryRun ? 0 : deleted, errors };
    };

    // 90일+ 미로그인 사용자 찾기
    const findInactiveUsers = async (): Promise<CleanupResult> => {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - 90);
        const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

        // lastLoginAt이 없거나 90일 이전인 사용자
        const snap = await getDocs(collection(Fire.store, 'users'));

        let found = 0;
        for (const docSnap of snap.docs) {
            const data = docSnap.data();
            const lastLogin = data.lastLoginAt?.toDate?.();
            if (!lastLogin || lastLogin < cutoffDate) {
                found++;
            }
        }

        return { found, deleted: 0, errors: [] };  // 사용자는 삭제하지 않음
    };

    const tasks: CleanupTask[] = [
        {
            id: 'notifications',
            name: 'Old Notifications',
            description: '180일 이상 된 알림 정리',
            icon: Bell,
            color: 'bg-yellow-500',
            action: cleanupOldNotifications,
        },
        {
            id: 'invites',
            name: 'Expired Invites',
            description: '만료된 룸 초대 정리',
            icon: FileText,
            color: 'bg-pink-500',
            action: cleanupExpiredInvites,
        },
        {
            id: 'users',
            name: 'Inactive Users',
            description: '90일+ 미로그인 사용자 탐지 (삭제 안 함)',
            icon: Users,
            color: 'bg-orange-500',
            action: findInactiveUsers,
        },
    ];

    const runTask = async (task: CleanupTask) => {
        setRunning(task.id);
        try {
            const result = await task.action();
            setResults(prev => ({ ...prev, [task.id]: result }));
        } catch (error: any) {
            setResults(prev => ({
                ...prev,
                [task.id]: { found: 0, deleted: 0, errors: [error.message] }
            }));
        } finally {
            setRunning(null);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">
                            <Trans>Cleanup Tools</Trans>
                        </h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            <Trans>Clean up old and orphaned data</Trans>
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={dryRun}
                                onChange={(e) => setDryRun(e.target.checked)}
                                className="rounded"
                            />
                            <span className="text-sm text-slate-600">Dry Run (미리보기)</span>
                        </label>
                        {!dryRun && (
                            <Badge variant="destructive">실제 삭제 모드</Badge>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                <div className="grid gap-4 max-w-4xl">
                    {tasks.map((task) => {
                        const Icon = task.icon;
                        const result = results[task.id];
                        const isRunning = running === task.id;

                        return (
                            <Card key={task.id} className="overflow-hidden">
                                <CardContent className="p-0">
                                    <div className="flex items-center">
                                        <div className={`w-16 ${task.color} h-full min-h-20 flex items-center justify-center`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <div className="flex-1 p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="font-semibold text-slate-900">
                                                        {task.name}
                                                    </h3>
                                                    <p className="text-sm text-slate-500">
                                                        {task.description}
                                                    </p>
                                                </div>
                                                <Button
                                                    onClick={() => runTask(task)}
                                                    disabled={isRunning}
                                                    variant={dryRun ? "outline" : "destructive"}
                                                    size="sm"
                                                >
                                                    {isRunning ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Play className="w-4 h-4" />
                                                    )}
                                                    <span className="ml-2">
                                                        {dryRun ? 'Scan' : 'Run'}
                                                    </span>
                                                </Button>
                                            </div>

                                            {/* Result */}
                                            {result && (
                                                <div className="mt-3 pt-3 border-t border-slate-100">
                                                    <div className="flex items-center gap-4 text-sm">
                                                        <span className="text-slate-600">
                                                            Found: <strong>{result.found}</strong>
                                                        </span>
                                                        {!dryRun && (
                                                            <span className="text-emerald-600">
                                                                Deleted: <strong>{result.deleted}</strong>
                                                            </span>
                                                        )}
                                                        {result.errors.length > 0 && (
                                                            <span className="text-red-600">
                                                                Errors: <strong>{result.errors.length}</strong>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Warning */}
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-4xl">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                            <h4 className="font-medium text-amber-800">주의사항</h4>
                            <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                                <li>삭제된 데이터는 복구할 수 없습니다.</li>
                                <li>먼저 Dry Run으로 영향 범위를 확인하세요.</li>
                                <li>대량 삭제는 시간이 걸릴 수 있습니다.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DevAdminCleanup;
